import React, { createContext, useContext, useState, useEffect, useCallback, useMemo, useRef } from 'react';
import { warehouseAPI, setWarehouseIdGetter, setWarehouseFallbackHandler } from '../lib/api';
import { useAuth } from './AuthContext';
import { toast } from 'sonner';

const WarehouseContext = createContext(null);

// Helper to get tenant-scoped storage key
const getStorageKey = (tenantId) => `warehouse.selected.${tenantId || 'default'}`;

export const useWarehouse = () => {
  const context = useContext(WarehouseContext);
  if (!context) {
    throw new Error('useWarehouse must be used within a WarehouseProvider');
  }
  return context;
};

export const WarehouseProvider = ({ children }) => {
  const { user, isAuthenticated } = useAuth();
  const tenantId = user?.tenant_id;
  
  const [warehouses, setWarehouses] = useState([]);
  const [selectedWarehouseId, setSelectedWarehouseIdState] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  
  // Track if we're currently handling a fallback (to prevent loops)
  const isFallingBack = useRef(false);

  // Derive selected warehouse from ID
  const selectedWarehouse = useMemo(() => {
    if (!selectedWarehouseId || warehouses.length === 0) return null;
    return warehouses.find(w => w.id === selectedWarehouseId) || null;
  }, [selectedWarehouseId, warehouses]);

  // Get the default warehouse
  const defaultWarehouse = useMemo(() => {
    return warehouses.find(w => w.is_default) || warehouses[0] || null;
  }, [warehouses]);

  // Fetch warehouses from API
  const refreshWarehouses = useCallback(async () => {
    if (!isAuthenticated) return;
    
    try {
      setError(null);
      const response = await warehouseAPI.listWarehouses();
      const warehouseList = response.data || [];
      setWarehouses(warehouseList);
      return warehouseList;
    } catch (err) {
      console.error('Failed to fetch warehouses:', err);
      setError('Failed to load warehouses');
      return [];
    }
  }, [isAuthenticated]);

  // Set selected warehouse ID with persistence
  const setSelectedWarehouseId = useCallback((warehouseId) => {
    if (!tenantId) return;
    
    setSelectedWarehouseIdState(warehouseId);
    
    // Persist to localStorage
    if (warehouseId) {
      localStorage.setItem(getStorageKey(tenantId), warehouseId);
    } else {
      localStorage.removeItem(getStorageKey(tenantId));
    }
  }, [tenantId]);

  // Handle warehouse unavailable error - fallback to default
  const handleWarehouseUnavailable = useCallback(async () => {
    // Prevent infinite loops
    if (isFallingBack.current) {
      console.warn('Already handling warehouse fallback, skipping');
      return null;
    }
    
    isFallingBack.current = true;
    
    try {
      // Refresh warehouse list to get latest state
      const warehouseList = await refreshWarehouses();
      
      // Find default warehouse
      const defaultWh = warehouseList.find(w => w.is_default && w.is_active) || 
                        warehouseList.find(w => w.is_active) ||
                        warehouseList[0];
      
      if (defaultWh && defaultWh.id !== selectedWarehouseId) {
        // Switch to default
        setSelectedWarehouseIdState(defaultWh.id);
        if (tenantId) {
          localStorage.setItem(getStorageKey(tenantId), defaultWh.id);
        }
        
        // Show toast
        toast.info('Selected warehouse is unavailable. Switched to default.', {
          duration: 4000
        });
        
        return defaultWh.id;
      }
      
      return null;
    } catch (err) {
      console.error('Failed to fallback to default warehouse:', err);
      return null;
    } finally {
      // Reset fallback flag after a short delay to allow retry
      setTimeout(() => {
        isFallingBack.current = false;
      }, 1000);
    }
  }, [refreshWarehouses, selectedWarehouseId, tenantId]);

  // Initialize: load warehouses and restore selection
  useEffect(() => {
    if (!isAuthenticated || !tenantId) {
      setLoading(false);
      return;
    }

    const initializeWarehouses = async () => {
      setLoading(true);
      
      try {
        const warehouseList = await refreshWarehouses();
        
        // Try to restore persisted selection
        const storedId = localStorage.getItem(getStorageKey(tenantId));
        
        if (storedId && warehouseList.length > 0) {
          // Validate stored warehouse still exists and is active
          const storedWarehouse = warehouseList.find(w => w.id === storedId && w.is_active);
          
          if (storedWarehouse) {
            setSelectedWarehouseIdState(storedId);
          } else {
            // Stored warehouse is invalid/archived - fallback to default
            const defaultWh = warehouseList.find(w => w.is_default) || warehouseList[0];
            if (defaultWh) {
              setSelectedWarehouseIdState(defaultWh.id);
              localStorage.setItem(getStorageKey(tenantId), defaultWh.id);
            }
          }
        } else if (warehouseList.length > 0) {
          // No stored selection - use default
          const defaultWh = warehouseList.find(w => w.is_default) || warehouseList[0];
          if (defaultWh) {
            setSelectedWarehouseIdState(defaultWh.id);
            localStorage.setItem(getStorageKey(tenantId), defaultWh.id);
          }
        }
      } catch (err) {
        console.error('Failed to initialize warehouses:', err);
        setError('Failed to load warehouses');
      } finally {
        setLoading(false);
      }
    };

    initializeWarehouses();
  }, [isAuthenticated, tenantId, refreshWarehouses]);

  // Register warehouse ID getter with API layer
  useEffect(() => {
    setWarehouseIdGetter(() => selectedWarehouseId);
    
    // Cleanup on unmount
    return () => {
      setWarehouseIdGetter(() => null);
    };
  }, [selectedWarehouseId]);

  // Register fallback handler with API layer
  useEffect(() => {
    setWarehouseFallbackHandler(handleWarehouseUnavailable);
    
    return () => {
      setWarehouseFallbackHandler(null);
    };
  }, [handleWarehouseUnavailable]);

  // Clear state on logout
  useEffect(() => {
    if (!isAuthenticated) {
      setWarehouses([]);
      setSelectedWarehouseIdState(null);
      setError(null);
    }
  }, [isAuthenticated]);

  const value = {
    // State
    warehouses,
    selectedWarehouseId,
    selectedWarehouse,
    defaultWarehouse,
    loading,
    error,
    
    // Actions
    setSelectedWarehouseId,
    refreshWarehouses,
    handleWarehouseUnavailable,
    
    // Helpers
    isMultiWarehouse: warehouses.length > 1,
    hasWarehouses: warehouses.length > 0,
  };

  return (
    <WarehouseContext.Provider value={value}>
      {children}
    </WarehouseContext.Provider>
  );
};

export default WarehouseContext;
